import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card,
  Button,
  Space,
  List,
  Avatar,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  message,
  Empty,
  Spin,
  Typography,
} from 'antd';
import {
  MessageOutlined,
  PlusOutlined,
  ReloadOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { messagesApi, show422Error, ValidationError } from '../../utils/api';
import { getImagePath } from '../../utils/imageHelper';
import PermissionGate from '../../components/PermissionGate';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';
import { useListReturnState } from '../../hooks/useListReturnState';

const Messages = () => {
  const [loading, setLoading] = useState(false);
  const [chats, setChats] = useState([]);
  const [users, setUsers] = useState([]);

  const [createOpen, setCreateOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [recipientLoading, setRecipientLoading] = useState(false);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createUsers, setCreateUsers] = useState([]);
  const [createForm] = Form.useForm();
  const userType = Form.useWatch('user_type', createForm);

  const captureCurrentListState = useCallback(() => ({}), []);

  const { navigateToDetail } = useListReturnState(captureCurrentListState, () => {});

  const userOptions = useMemo(() => {
    return (createUsers || []).map((u) => ({
      value: u.id,
      label: u.value || u.name,
      image: u.image,
      link: u.link || '',
    }));
  }, [createUsers]);

  const recipientOptions = useMemo(() => {
    if (!userType) return [];
    if (userType === 'client') {
      return userOptions.filter((u) => String(u.link).toLowerCase().includes('/clients/'));
    }
    return userOptions.filter((u) => !String(u.link).toLowerCase().includes('/clients/'));
  }, [userOptions, userType]);

  const fetchIndex = async () => {
    setLoading(true);
    try {
      const res = await messagesApi.getAll();
      const data = res?.data ?? res;
      setChats(data?.chats || []);
      setUsers(data?.users || []);
    } catch (e) {
      message.error(e?.message || 'خطا در دریافت پیام‌ها');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIndex();
  }, []);

  const loadCreateUsers = async (type) => {
    setRecipientLoading(true);
    try {
      const res = await messagesApi.getCreateData(type);
      const data = res?.data ?? res;
      setCreateUsers(data?.users || []);
    } finally {
      setRecipientLoading(false);
    }
  };

  const openCreateModal = async () => {
    setCreateOpen(true);
    createForm.resetFields();
    createForm.setFieldsValue({ user_type: 'employee' });
    setCreateLoading(true);
    try {
      await loadCreateUsers('employee');
    } catch (e) {
      message.error(e?.message || 'خطا در دریافت فرم پیام');
      setCreateOpen(false);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleUserTypeChange = async (nextType) => {
    createForm.setFieldsValue({ user_id: undefined, client_id: undefined });
    try {
      await loadCreateUsers(nextType);
    } catch (e) {
      message.error(e?.message || 'خطا در دریافت لیست گیرندگان');
    }
  };

  const submitCreate = async () => {
    try {
      const values = await createForm.validateFields();
      const payload = {
        user_type: values.user_type,
        user_id: values.user_type === 'employee' ? values.user_id : null,
        client_id: values.user_type === 'client' ? values.client_id : null,
        message: values.message,
        types: 'modal',
      };
      setCreateSubmitting(true);
      await messagesApi.create(payload);
      message.success('پیام ثبت شد');
      setCreateOpen(false);
      fetchIndex();
    } catch (e) {
      if (e?.errorFields) return;
      if (e instanceof ValidationError || e?.error?.details) show422Error(e, createForm);
      else message.error(e?.message || 'خطا در ثبت پیام');
    } finally {
      setCreateSubmitting(false);
    }
  };

  const getPeer = (row) => {
    const from = row?.from_user;
    const to = row?.to_user;
    // برای نمایش ایندکس: طرف مقابل پیام
    if (from?.id === row?.from) return to || from;
    return from || to;
  };

  return (
    <MainLayout>
      <div className="custom-font" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-12 h-12 rounded-xl bg-cyan-50 dark:bg-cyan-900/20 flex items-center justify-center">
                <MessageOutlined className="text-cyan-600 dark:text-cyan-400 text-xl" />
              </div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-800 dark:text-gray-100 m-0 truncate">پیام‌ها</h1>
            </div>
            <Space wrap>
              <Button icon={<ReloadOutlined />} onClick={fetchIndex} loading={loading}>
                بروزرسانی
              </Button>
              <PermissionGate permission="add_message">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  className={PANEL_PRIMARY_BTN_CLASS}
                  onClick={openCreateModal}
                >
                  پیام جدید
                </Button>
              </PermissionGate>
            </Space>
          </div>

          <div className="p-3 sm:p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            <Card className="shadow-sm border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
              <List
                loading={loading}
                dataSource={chats}
                locale={{ emptyText: <Empty description="پیامی ثبت نشده است" /> }}
                renderItem={(row) => {
                  const peer = getPeer(row) || {};
                  const fromUser = row?.from_user || {};
                  const toUser = row?.to_user || {};
                  return (
                    <List.Item
                      className="!items-start"
                      actions={[
                        <Button
                          key="view"
                          type="text"
                          icon={<EyeOutlined />}
                          onClick={() => navigateToDetail(`/messages/${row.user_id}`)}
                          className="!px-1 sm:!px-2"
                        >
                          مشاهده گفتگو
                        </Button>,
                      ]}
                    >
                      <List.Item.Meta
                        avatar={
                          <Avatar src={getImagePath(peer.image_url || peer.image)}>
                            {(peer.name || '?').charAt(0)}
                          </Avatar>
                        }
                        title={
                          <Space wrap>
                            <span>{peer.name || '—'}</span>
                            {row.message_seen === 'no' ? <Tag color="red">خوانده‌نشده</Tag> : null}
                            {/* <Tag color="blue">#چت {row.id}</Tag> */}
                          </Space>
                        }
                        description={
                          <Space direction="vertical" size={2}>
                            <Typography.Text className="break-words">{row.message || '—'}</Typography.Text>
                            <Space size={6} wrap>
                              <Tag>فرستنده: {fromUser.name || '-'}</Tag>
                              <Tag>گیرنده: {toUser.name || '-'}</Tag>
                              {/* <Tag>موبایل: {peer.mobile_with_phonecode || '--'}</Tag> */}
                              {/* <Tag color="purple">
                                unread: {peer.unread_messages_count ?? 0}
                              </Tag> */}
                            </Space>
                          </Space>
                        }
                      />
                    </List.Item>
                  );
                }}
              />
            </Card>
          </div>
        </Card>

        <Modal
          title="پیام جدید"
          open={createOpen}
          onCancel={() => setCreateOpen(false)}
          onOk={submitCreate}
          confirmLoading={createSubmitting}
          okText="ارسال"
          cancelText="لغو"
          okButtonProps={{ className: PANEL_PRIMARY_BTN_CLASS }}
          destroyOnClose
        >
          <Form form={createForm} layout="vertical" className="custom-font">
            <Form.Item name="user_type" label="نوع گیرنده" rules={[{ required: true }]}>
              <Select popupClassName="custom-font" onChange={handleUserTypeChange}>
                <Select.Option value="employee">کارمند</Select.Option>
                <Select.Option value="client">مشتری</Select.Option>
              </Select>
            </Form.Item>

            {userType === 'client' ? (
              <Form.Item name="client_id" label="مشتری" rules={[{ required: true }]}>
                <Select
                  showSearch
                  optionFilterProp="children"
                  loading={createLoading || recipientLoading}
                  disabled={createLoading || recipientLoading || !userType}
                  notFoundContent={createLoading || recipientLoading ? <Spin size="small" /> : null}
                  popupClassName="custom-font"
                >
                  {recipientOptions.map((u) => (
                    <Select.Option key={u.value} value={u.value}>
                      {u.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            ) : (
              <Form.Item name="user_id" label="کارمند" rules={[{ required: true }]}>
                <Select
                  showSearch
                  optionFilterProp="children"
                  loading={createLoading || recipientLoading}
                  disabled={createLoading || recipientLoading || !userType}
                  notFoundContent={createLoading || recipientLoading ? <Spin size="small" /> : null}
                  popupClassName="custom-font"
                >
                  {recipientOptions.map((u) => (
                    <Select.Option key={u.value} value={u.value}>
                      {u.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            )}

            <Form.Item name="message" label="متن پیام" rules={[{ required: true }]}>
              <Input.TextArea rows={4} />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default Messages;
